import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { generateText } from "ai";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createLovableAiGatewayProvider } from "@/lib/ai-gateway.server";
import { AI_MODELS } from "./ai-models";

const Input = z.object({
  imageBase64: z.string().min(100).max(15_000_000),
  mediaType: z.string().default("image/jpeg"),
});

const Row = z.object({
  day_of_week: z.number().int().min(1).max(7),
  period: z.number().int().min(1).max(12),
  start_time: z.string().regex(/^\d{2}:\d{2}$/),
  end_time: z.string().regex(/^\d{2}:\d{2}$/),
  subject: z.string().min(1).max(60),
  room: z.string().max(30).optional().nullable(),
});
const Schema = z.object({ rows: z.array(Row).min(1).max(84) });

export type ExtractedRow = z.infer<typeof Row>;

function extractJson(raw: string): unknown {
  const cleaned = raw.replace(/^\s*```(?:json)?\s*/i, "").replace(/\s*```\s*$/i, "").trim();
  const start = cleaned.indexOf("{");
  const end = cleaned.lastIndexOf("}");
  const slice = start >= 0 && end > start ? cleaned.slice(start, end + 1) : cleaned;
  return JSON.parse(slice);
}

export const extractScheduleFromImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => Input.parse(d))
  .handler(async ({ data, context }) => {
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Servizio AI non configurato");
    const provider = createLovableAiGatewayProvider(key);

    const system = `Sei un OCR specializzato in orari scolastici italiani. Estrai la tabella dell'orario dalla foto.
Restituisci SOLO JSON valido con questa forma:
{ "rows": [ { "day_of_week": 1-7 (1=lunedì, 7=domenica), "period": 1-8, "start_time": "HH:MM", "end_time": "HH:MM", "subject": "Materia", "room": "opzionale" } ] }

Regole:
- Salta ricreazioni/pause e celle vuote.
- Se una materia è abbreviata (es. "Mat"), espandi se ovvio (Matematica), altrimenti lascia così com'è.
- Le materie con lo stesso nome consecutive (doppia ora) sono DUE righe con period diversi, non una.
- Se manca l'orario esplicito, deducilo assumendo che la prima ora inizi alle 08:00 e duri 55 minuti + pausa breve.
- Rispondi SOLO con il JSON, senza commenti né markdown.`;

    const bin = Uint8Array.from(atob(data.imageBase64), (c) => c.charCodeAt(0));
    const { text } = await generateText({
      model: provider(AI_MODELS.VISION),
      system,
      messages: [
        {
          role: "user",
          content: [
            { type: "text", text: "Estrai l'orario da questa foto." },
            { type: "image", image: bin, mediaType: data.mediaType },
          ],
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ] as any,
    });
    const parsed = Schema.parse(extractJson(text));
    return parsed;
  });